/**
 * Blog listing with pagination and scroll position management
 * Handles paginated blog post display with URL sync and scroll history restoration
 */

import { h, render } from 'preact'
import { useState, useEffect } from 'preact/hooks'
import { PaginationControls } from 'libs/athos/components/PaginationControls'
import { BlogCard } from './BlogCard'

const data = window.globals?.currentBlog?.articles || []

function usePageSync() {
  const [currentPage, setCurrentPage] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const pageParam = urlParams.get('page')
    return pageParam ? parseInt(pageParam, 10) : 1
  })

  useEffect(() => {
    const url = new URL(window.location.href)
    if (currentPage > 1) {
      url.searchParams.set('page', currentPage.toString())
    } else {
      url.searchParams.delete('page')
    }
    if (url.href !== window.location.href) {
      window.history.pushState({ page: currentPage }, '', url.toString())
    }
  }, [currentPage])

  useEffect(() => {
    const onPopState = (event) => {
      const urlParams = new URLSearchParams(window.location.search)
      const pageParam = urlParams.get('page')
      const newPage = event.state?.page ?? (pageParam ? parseInt(pageParam, 10) : 1)
      setCurrentPage(newPage)
    }
    window.addEventListener('popstate', onPopState)
    return () => {
      window.removeEventListener('popstate', onPopState)
    }
  }, [])

  return [currentPage, setCurrentPage]
}

function BlogGrid() {
  history.scrollRestoration = 'manual'
  const [currentPage, setCurrentPage] = usePageSync()

  const container = document.getElementById('list-blogs')
  const resultsPerPage = parseInt(container?.getAttribute('per-page') || 9)
  const totalPages = Math.max(1, Math.ceil(data.length / resultsPerPage))
  const activePage = Math.min(Math.max(1, currentPage), totalPages)
  const start = (activePage - 1) * resultsPerPage
  const currentBlogs = data.slice(start, start + resultsPerPage)

  useEffect(() => {
    if (currentPage !== activePage) setCurrentPage(activePage)
  }, [totalPages])

  if (data.length === 0) {
    return <div><p className="paragraph-m">No blog posts found.</p></div>
  }

  return (
    <div className="grow flex flex-col px-margin py-1.5 md:py-3.5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-gutter gap-y-1.5 md:gap-y-2.5 w-full">
        {currentBlogs.map((blog, index) => {
          return <BlogCard blog={blog} key={blog.id} dataIndex={index.toString()} />
        })}
      </div>
      <PaginationControls activePage={activePage} totalPages={totalPages} onPageChange={setCurrentPage} />
    </div>
  )
}

function initBlogGrid() {
  const container = document.getElementById('list-blogs')
  if (container && window.globals?.currentBlog?.articles) {
    render(<BlogGrid />, container)
  } else {
    setTimeout(initBlogGrid, 100)
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBlogGrid)
} else {
  initBlogGrid()
}
